import React, { useState, useEffect, useMemo } from 'react';
import {
  Pencil,
  Trash2,
  PlusCircle,
  UserCog,
  Search,
  Database,
  Cloud,
  Server,
  MessageSquare,
  Mail,
  Users,
  Building2,
  Share2,
  Phone,
  Globe,
  Sparkles,
  KeyRound,
  Activity,
  CreditCard,
  Plug,
  X,
} from 'lucide-react';
import GenericConnectorForm from './components/GenericConnectorForm';
import ConnectorTypeSelector from './components/ConnectorTypeSelector';
import PermissionsAssignmentModal from '../common/Permissions/PermissionsAssignmentModal';
import { apiService } from './connectionsApiService';
import ConfirmDialog from '../Dialogs/ConfirmDialog';
import { useTheme } from '../../../components/common/Theme';
import {
  CONNECTION_CATEGORIES,
  CONNECTORS,
  getCategoryLabel,
  getConnector,
  getConnectorDisplayName,
  type ConnectionCategoryId,
  type DataStoreConnection,
} from './catalog';

const CATEGORY_ICONS: Record<ConnectionCategoryId | 'other', React.ReactNode> = {
  databases: <Database className="w-3.5 h-3.5" />,
  cloud_storage: <Cloud className="w-3.5 h-3.5" />,
  file_transfer: <Server className="w-3.5 h-3.5" />,
  messaging: <MessageSquare className="w-3.5 h-3.5" />,
  email: <Mail className="w-3.5 h-3.5" />,
  collaboration: <Users className="w-3.5 h-3.5" />,
  crm_erp: <Building2 className="w-3.5 h-3.5" />,
  social: <Share2 className="w-3.5 h-3.5" />,
  voice_telephony: <Phone className="w-3.5 h-3.5" />,
  apis: <Globe className="w-3.5 h-3.5" />,
  ai_services: <Sparkles className="w-3.5 h-3.5" />,
  identity: <KeyRound className="w-3.5 h-3.5" />,
  observability: <Activity className="w-3.5 h-3.5" />,
  payments: <CreditCard className="w-3.5 h-3.5" />,
  other: <Plug className="w-3.5 h-3.5" />,
};

const CATEGORY_ICON_TINT: Record<ConnectionCategoryId | 'other', string> = {
  databases: 'bg-blue-50 text-blue-600',
  cloud_storage: 'bg-sky-50 text-sky-600',
  file_transfer: 'bg-violet-50 text-violet-600',
  messaging: 'bg-indigo-50 text-indigo-600',
  email: 'bg-rose-50 text-rose-600',
  collaboration: 'bg-teal-50 text-teal-600',
  crm_erp: 'bg-amber-50 text-amber-600',
  social: 'bg-pink-50 text-pink-600',
  voice_telephony: 'bg-cyan-50 text-cyan-600',
  apis: 'bg-emerald-50 text-emerald-600',
  ai_services: 'bg-fuchsia-50 text-fuchsia-600',
  identity: 'bg-slate-100 text-slate-600',
  observability: 'bg-orange-50 text-orange-600',
  payments: 'bg-lime-50 text-lime-700',
  other: 'bg-slate-100 text-slate-500',
};

const LOG_PREFIX = '[ConnectionsPage]';
function logConnections(
  level: 'app' | 'warning' | 'error',
  event: string,
  extra?: Record<string, unknown>
): void {
  const payload = extra ? { event, ...extra } : { event };
  if (level === 'error') {
    console.error(LOG_PREFIX, '[error]', payload);
    return;
  }
  if (level === 'warning') {
    console.warn(LOG_PREFIX, '[warning]', payload);
    return;
  }
  console.info(LOG_PREFIX, '[app]', payload);
}

interface ConnectionsPageProps {
  /** Render as nested overlay (e.g. add-source from ingestion). Standalone route omits this. */
  embedMode?: boolean;
  onDismiss?: () => void;
  onConnectionSaved?: (saved?: DataStoreConnection | null) => void;
}

const CATEGORY_ICON_TINT_DARK: Record<ConnectionCategoryId | 'other', string> = {
  databases: 'bg-blue-500/15 text-blue-400',
  cloud_storage: 'bg-sky-500/15 text-sky-400',
  file_transfer: 'bg-violet-500/15 text-violet-400',
  messaging: 'bg-indigo-500/15 text-indigo-400',
  email: 'bg-rose-500/15 text-rose-400',
  collaboration: 'bg-teal-500/15 text-teal-400',
  crm_erp: 'bg-amber-500/15 text-amber-400',
  social: 'bg-pink-500/15 text-pink-400',
  voice_telephony: 'bg-cyan-500/15 text-cyan-400',
  apis: 'bg-emerald-500/15 text-emerald-400',
  ai_services: 'bg-fuchsia-500/15 text-fuchsia-400',
  identity: 'bg-slate-500/15 text-slate-300',
  observability: 'bg-orange-500/15 text-orange-400',
  payments: 'bg-lime-500/15 text-lime-400',
  other: 'bg-neutral-800 text-neutral-400',
};

const ConnectionsPage: React.FC<ConnectionsPageProps> = ({
  embedMode = false,
  onDismiss,
  onConnectionSaved,
}) => {
  const { theme } = useTheme();
  const isLight = theme === 'white';

  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const [selectedConnector, setSelectedConnector] = useState<string | null>(null);
  const [editingConnection, setEditingConnection] = useState<DataStoreConnection | null>(null);
  const [connections, setConnections] = useState<DataStoreConnection[]>([]);
  const [showAssignmentModal, setAssignmentModal] = useState(false);
  const [permissionTarget, setPermissionTarget] = useState<DataStoreConnection | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<ConnectionCategoryId | 'other' | null>(null);
  const [selectorCategory, setSelectorCategory] = useState<ConnectionCategoryId | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editLoadingId, setEditLoadingId] = useState<string | null>(null);

  const fetchConnections = async () => {
    try {
      setLoadError(null);
      const data = await apiService.get('/DataStores', {}, 'backend');
      const list = Array.isArray(data?.datastores)
        ? data.datastores
        : Array.isArray(data)
          ? data
          : null;
      if (list) {
        setConnections(list);
        logConnections('app', 'fetch_connections_ok', { count: list.length, embedMode });
      } else {
        logConnections('warning', 'fetch_connections_unexpected_shape');
      }
    } catch (err) {
      logConnections('error', 'fetch_connections_failed', {
        message: err instanceof Error ? err.message : String(err),
      });
      setLoadError('Failed to load connections. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConnections();
  }, []);

  useEffect(() => {
    if (!embedMode) return;
    logConnections('app', 'embed_add_flow_opened');
    setSelectorCategory(null);
    setShowTypeSelector(true);
    setSelectedConnector(null);
    setEditingConnection(null);
  }, [embedMode]);

  const resolveConnectionCategory = (connectorKey?: string): ConnectionCategoryId | 'other' => {
    if (!connectorKey) return 'other';
    return getConnector(connectorKey)?.category ?? 'other';
  };

  const filteredConnections = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return connections.filter((conn) => {
      const category = resolveConnectionCategory(conn.connectorKey);
      if (categoryFilter && category !== categoryFilter) return false;
      if (!q) return true;
      const displayName = getConnectorDisplayName(conn.connectorKey).toLowerCase();
      const categoryLabel =
        category === 'other' ? 'other' : getCategoryLabel(category).toLowerCase();
      return (
        conn.name?.toLowerCase().includes(q) ||
        conn.connectorKey?.toLowerCase().includes(q) ||
        displayName.includes(q) ||
        categoryLabel.includes(q) ||
        (conn.tags || []).some((t) => t.toLowerCase().includes(q)) ||
        (conn.environment || '').toLowerCase().includes(q)
      );
    });
  }, [connections, searchQuery, categoryFilter]);

  const categoriesInUse = useMemo(() => {
    const ids = new Set<ConnectionCategoryId | 'other'>();
    connections.forEach((c) => {
      ids.add(resolveConnectionCategory(c.connectorKey));
    });
    const known = CONNECTION_CATEGORIES.filter((c) => ids.has(c.id));
    if (ids.has('other')) {
      return [
        ...known,
        { id: 'other' as const, label: 'Other', description: 'Unrecognized or legacy connectors' },
      ];
    }
    return known;
  }, [connections]);

  const handleAddConnection = (saved?: DataStoreConnection | null) => {
    fetchConnections();

    // Teams create-then-fetch: keep the open form mounted so channels can be loaded
    // without wiping in-progress credentials (form holds connectionId locally).
    const isTeamsCreate =
      !editingConnection?.id &&
      Boolean(saved?.id) &&
      (saved?.connectorKey === 'microsoftTeams' ||
        selectedConnector === 'microsoftTeams' ||
        selectedConnector === 'ms-team');

    if (isTeamsCreate) {
      logConnections('app', 'teams_create_keep_form', { connectionId: saved?.id });
      setShowTypeSelector(false);
      setSelectorCategory(null);
      return;
    }

    if (embedMode) {
      logConnections('app', 'embed_connection_saved', {
        connectionId: saved?.id,
        name: saved?.name,
        connectorKey: saved?.connectorKey,
      });
      onConnectionSaved?.(saved);
      return;
    }

    setSelectedConnector(null);
    setEditingConnection(null);
    // Successful create: dismiss the whole add flow (picker + form)
    setShowTypeSelector(false);
    setSelectorCategory(null);
  };

  const closeTypeSelector = () => {
    setShowTypeSelector(false);
    setSelectorCategory(null);
    setSelectedConnector(null);
    if (embedMode) {
      logConnections('app', 'embed_add_flow_dismissed');
      onDismiss?.();
    }
  };

  const closeConnectorForm = () => {
    // Canceling the config form returns to the Add connection picker when
    // that flow is still active; edit flow just closes the form.
    setSelectedConnector(null);
    setEditingConnection(null);
  };

  const openEdit = async (conn: DataStoreConnection) => {
    if (!conn?.id || editLoadingId) return;
    setEditLoadingId(conn.id);
    try {
      const detail = await apiService.get(`/DataStores/${conn.id}`, {}, 'backend');
      setEditingConnection(
        detail && typeof detail === 'object' ? { ...conn, ...detail, id: detail.id || conn.id } : conn
      );
      setSelectedConnector(null);
      logConnections('app', 'fetch_connection_detail_ok', { id: conn.id });
    } catch (err) {
      logConnections('error', 'fetch_connection_detail_failed', {
        id: conn.id,
        message: err instanceof Error ? err.message : String(err),
      });
      setEditingConnection(conn);
      setSelectedConnector(null);
    } finally {
      setEditLoadingId(null);
    }
  };

  const openAddFlow = (category?: ConnectionCategoryId | null) => {
    setSelectorCategory(category ?? null);
    setShowTypeSelector(true);
    setSelectedConnector(null);
    setEditingConnection(null);
  };

  const isActive = (status?: string) => (status || '').toLowerCase() === 'active';

  const page = (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className={`text-xl font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`}>
            Connections
          </h2>
          <p className={`mt-0.5 text-sm ${isLight ? 'text-slate-500' : 'text-gray-400'}`}>
            Manage data sources, channels, and tools used by agents.
          </p>
        </div>
        <button
          type="button"
          onClick={() => openAddFlow(null)}
          className="inline-flex items-center gap-2 self-start rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-700"
        >
          <PlusCircle className="h-4 w-4" />
          Add connection
        </button>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 gap-3 sm:max-w-md">
        <div
          className={`rounded-lg border p-3 ${
            isLight
              ? 'border-slate-200 bg-white shadow-sm'
              : 'border-neutral-800 bg-neutral-900/60'
          }`}
        >
          <div
            className={`text-lg font-semibold tabular-nums ${
              isLight ? 'text-slate-900' : 'text-white'
            }`}
          >
            {connections.length}
          </div>
          <div className={`text-xs ${isLight ? 'text-slate-500' : 'text-gray-400'}`}>
            Configured
          </div>
        </div>
        <div
          className={`rounded-lg border p-3 ${
            isLight
              ? 'border-slate-200 bg-white shadow-sm'
              : 'border-neutral-800 bg-neutral-900/60'
          }`}
        >
          <div
            className={`text-lg font-semibold tabular-nums ${
              isLight ? 'text-indigo-600' : 'text-indigo-400'
            }`}
          >
            {CONNECTORS.length}
          </div>
          <div className={`text-xs ${isLight ? 'text-slate-500' : 'text-gray-400'}`}>
            Providers available
          </div>
        </div>
      </div>

      {/* Panel */}
      <div
        className={`overflow-hidden rounded-xl border shadow-sm ${
          isLight
            ? 'border-slate-200 bg-white'
            : 'border-neutral-800 bg-neutral-900/60'
        }`}
      >
        {/* Toolbar */}
        <div
          className={`flex flex-col gap-3 border-b px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${
            isLight ? 'border-slate-100' : 'border-neutral-800'
          }`}
        >
          <div className="relative w-full sm:max-w-xs">
            <Search
              className={`pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 ${
                isLight ? 'text-slate-400' : 'text-gray-500'
              }`}
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search connections…"
              className={`h-9 w-full rounded-lg border pl-9 pr-3 text-sm outline-none transition focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 ${
                isLight
                  ? 'border-slate-200 bg-white text-slate-900 placeholder:text-slate-400'
                  : 'border-neutral-700 bg-neutral-950 text-white placeholder:text-gray-500'
              }`}
            />
          </div>

          {categoriesInUse.length > 0 && (
            <div className="flex gap-1.5 overflow-x-auto pb-0.5">
              <button
                type="button"
                onClick={() => setCategoryFilter(null)}
                className={`shrink-0 rounded-lg px-2.5 py-1.5 text-xs font-medium transition ${
                  categoryFilter === null
                    ? 'bg-indigo-600 text-white'
                    : isLight
                      ? 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      : 'bg-neutral-800 text-gray-400 hover:bg-neutral-700 hover:text-gray-200'
                }`}
              >
                All
              </button>
              {categoriesInUse.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() =>
                    setCategoryFilter(categoryFilter === cat.id ? null : cat.id)
                  }
                  className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition ${
                    categoryFilter === cat.id
                      ? 'bg-indigo-600 text-white'
                      : isLight
                        ? 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        : 'bg-neutral-800 text-gray-400 hover:bg-neutral-700 hover:text-gray-200'
                  }`}
                >
                  {CATEGORY_ICONS[cat.id]}
                  {cat.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-xs">
            <thead>
              <tr
                className={`border-b ${
                  isLight
                    ? 'border-slate-200 bg-slate-50/80'
                    : 'border-neutral-800 bg-neutral-950/80'
                }`}
              >
                {['Name', 'Provider', 'Category', 'Status', 'Environment', 'Updated', ''].map(
                  (label, i) => (
                    <th
                      key={label || 'actions'}
                      className={`px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap ${
                        isLight ? 'text-slate-500' : 'text-gray-500'
                      } ${i === 2 ? 'hidden md:table-cell' : ''} ${
                        i === 4 ? 'hidden lg:table-cell' : ''
                      } ${i === 5 ? 'hidden xl:table-cell' : ''} ${
                        i === 6 ? 'text-right' : 'text-left'
                      }`}
                    >
                      {label || 'Actions'}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td
                    colSpan={7}
                    className={`px-4 py-14 text-center text-sm ${
                      isLight ? 'text-slate-500' : 'text-gray-500'
                    }`}
                  >
                    Loading connections…
                  </td>
                </tr>
              )}

              {!loading && loadError && connections.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-14 text-center text-sm text-red-500">
                    {loadError}
                  </td>
                </tr>
              )}

              {!loading &&
                filteredConnections.length === 0 &&
                !(loadError && connections.length === 0) && (
                  <tr>
                    <td colSpan={7} className="px-4 py-14 text-center">
                      <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-gray-400'}`}>
                        {connections.length === 0
                          ? 'No connections yet. Add your first integration.'
                          : 'No connections match your filters.'}
                      </p>
                      <div className="mt-4 flex items-center justify-center gap-3">
                        {connections.length > 0 && categoryFilter && (
                          <button
                            type="button"
                            onClick={() => setCategoryFilter(null)}
                            className={`text-sm ${
                              isLight
                                ? 'text-slate-500 hover:text-slate-800'
                                : 'text-gray-400 hover:text-white'
                            }`}
                          >
                            Clear filter
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() =>
                            openAddFlow(
                              categoryFilter && categoryFilter !== 'other'
                                ? categoryFilter
                                : null
                            )
                          }
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-500"
                        >
                          <PlusCircle className="h-4 w-4" />
                          Add connection
                        </button>
                      </div>
                    </td>
                  </tr>
                )}

              {!loading &&
                filteredConnections.map((conn) => {
                  const category = resolveConnectionCategory(conn.connectorKey);
                  const categoryLabel =
                    category === 'other' ? 'Other' : getCategoryLabel(category);
                  const providerLabel = getConnectorDisplayName(conn.connectorKey);
                  const active = isActive(conn.status);
                  const iconTint = isLight
                    ? CATEGORY_ICON_TINT[category]
                    : CATEGORY_ICON_TINT_DARK[category];

                  return (
                    <tr
                      key={conn.id}
                      className={`border-b transition-colors ${
                        isLight
                          ? 'border-slate-100 hover:bg-slate-50/70'
                          : 'border-neutral-800/80 hover:bg-indigo-500/[0.04]'
                      }`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex min-w-0 items-center gap-3">
                          <div
                            className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${iconTint}`}
                          >
                            {CATEGORY_ICONS[category]}
                          </div>
                          <div className="min-w-0">
                            <div
                              className={`truncate text-[13px] font-medium ${
                                isLight ? 'text-slate-900' : 'text-white'
                              }`}
                            >
                              {conn.name}
                            </div>
                            {conn.tags && conn.tags.length > 0 && (
                              <div
                                className={`mt-0.5 truncate text-[11px] ${
                                  isLight ? 'text-slate-400' : 'text-gray-500'
                                }`}
                              >
                                {conn.tags.join(', ')}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-md px-2 py-0.5 text-[11px] font-medium ${
                            isLight
                              ? 'bg-indigo-50 text-indigo-700'
                              : 'bg-indigo-500/10 text-indigo-300 ring-1 ring-indigo-500/20'
                          }`}
                        >
                          {providerLabel}
                        </span>
                      </td>
                      <td className="hidden px-4 py-3 md:table-cell">
                        <span className={isLight ? 'text-slate-600' : 'text-gray-400'}>
                          {categoryLabel}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                            active
                              ? isLight
                                ? 'bg-emerald-50 text-emerald-700'
                                : 'bg-green-500/10 text-green-400'
                              : isLight
                                ? 'bg-rose-50 text-rose-700'
                                : 'bg-red-500/10 text-red-400'
                          }`}
                        >
                          <span
                            className={`h-1.5 w-1.5 rounded-full ${
                              active
                                ? isLight
                                  ? 'bg-emerald-500'
                                  : 'bg-green-400'
                                : isLight
                                  ? 'bg-rose-500'
                                  : 'bg-red-400'
                            }`}
                          />
                          {conn.status || 'Unknown'}
                        </span>
                      </td>
                      <td className="hidden px-4 py-3 lg:table-cell">
                        <span
                          className={`font-mono text-[11px] ${
                            isLight ? 'text-slate-500' : 'text-gray-400'
                          }`}
                        >
                          {conn.environment || '—'}
                        </span>
                      </td>
                      <td className="hidden px-4 py-3 xl:table-cell">
                        <span className={isLight ? 'text-slate-400' : 'text-gray-500'}>
                          {conn.updatedAt
                            ? new Date(conn.updatedAt).toLocaleDateString()
                            : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-0.5">
                          <button
                            type="button"
                            title="Permissions"
                            onClick={() => {
                              setPermissionTarget(conn);
                              setAssignmentModal(true);
                            }}
                            className={`rounded-md p-1.5 transition ${
                              isLight
                                ? 'text-slate-400 hover:bg-slate-100 hover:text-indigo-600'
                                : 'text-gray-500 hover:bg-neutral-800 hover:text-indigo-400'
                            }`}
                          >
                            <UserCog className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            title="Edit"
                            disabled={editLoadingId === conn.id}
                            onClick={() => void openEdit(conn)}
                            className={`rounded-md p-1.5 transition ${
                              isLight
                                ? 'text-slate-400 hover:bg-slate-100 hover:text-amber-600'
                                : 'text-gray-500 hover:bg-neutral-800 hover:text-yellow-400'
                            }`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            title="Delete"
                            onClick={() => setDeleteId(conn.id)}
                            className={`rounded-md p-1.5 transition ${
                              isLight
                                ? 'text-slate-400 hover:bg-rose-50 hover:text-rose-600'
                                : 'text-gray-500 hover:bg-neutral-800 hover:text-red-400'
                            }`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        {!loading && filteredConnections.length > 0 && (
          <div
            className={`border-t px-4 py-2.5 text-[11px] ${
              isLight
                ? 'border-slate-100 text-slate-400'
                : 'border-neutral-800 text-gray-500'
            }`}
          >
            Showing {filteredConnections.length}
            {filteredConnections.length !== connections.length
              ? ` of ${connections.length}`
              : ''}{' '}
            connection{filteredConnections.length === 1 ? '' : 's'}
          </div>
        )}
      </div>
    </div>
  );

  const overlays = (
    <>
      {showTypeSelector && (
        <div className={selectedConnector ? 'hidden' : undefined} aria-hidden={!!selectedConnector}>
          <ConnectorTypeSelector
            initialCategory={selectorCategory}
            onClose={closeTypeSelector}
            onSelect={(connectorKey) => {
              // Open config form on top; keep picker mounted so Cancel returns here
              setSelectedConnector(connectorKey);
              setEditingConnection(null);
            }}
            currentSelection={selectedConnector || ''}
          />
        </div>
      )}

      {(selectedConnector || editingConnection) && (
        <GenericConnectorForm
          connectorKey={selectedConnector || editingConnection?.connectorKey || ''}
          onClose={closeConnectorForm}
          onSubmit={handleAddConnection}
          initialData={editingConnection}
        />
      )}

      {showAssignmentModal && (
        <PermissionsAssignmentModal
          onClose={() => {
            setAssignmentModal(false);
            setPermissionTarget(null);
          }}
          onSave={() => {}}
          entityType="connections"
          entityName={permissionTarget?.name || 'Connection & Keys'}
        />
      )}

      {deleteId && (
        <ConfirmDialog
          title="Delete Connection"
          message="Are you sure you want to delete this connection? This action will permanently remove it from the system."
          onYes={async () => {
            if (deleteId) {
              await apiService.delete(`/DataStores/${deleteId}`, 'backend');
              await fetchConnections();
              setDeleteId(null);
            }
          }}
          onCancel={() => setDeleteId(null)}
        />
      )}
    </>
  );

  if (!embedMode) {
    return (
      <>
        {page}
        {overlays}
      </>
    );
  }

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
      <div
        className={`relative flex w-full max-w-5xl max-h-[90vh] flex-col overflow-hidden rounded-xl border shadow-2xl ${
          isLight ? 'border-slate-200 bg-white' : 'border-neutral-700/80 bg-neutral-900/95'
        }`}
      >
        <div
          className={`flex shrink-0 items-center justify-between border-b px-4 py-3 ${
            isLight ? 'border-slate-200' : 'border-neutral-800'
          }`}
        >
          <h2 className={`text-sm font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`}>
            Add connection
          </h2>
          <button
            type="button"
            onClick={() => {
              logConnections('app', 'embed_overlay_closed');
              onDismiss?.();
            }}
            className={`rounded-lg p-1.5 transition-colors ${
              isLight
                ? 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
                : 'text-gray-400 hover:bg-neutral-800 hover:text-white'
            }`}
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">{page}</div>
      </div>
      {overlays}
    </div>
  );
};

export default ConnectionsPage;
